/**
 * The data behind `BurnDownChart`, kept apart from the component so it can be tested without a renderer.
 *
 * A burn-down of SCHEDULED payments (feature 024): it starts at the period's total for dated payments and
 * steps down by each date's total. Every value is CENTS as a number — the caller converts from its exact
 * `Valuation` at the edge, as every other chart here does — and every date is a `YYYY-MM-DD` string.
 */

export interface BurnDownStep {
  /** The due date. */
  readonly at: string;
  /** What leaves on that date, in cents. */
  readonly amountCents: number;
}

export interface BurnDownPoint {
  /** UTC midnight of the day, in milliseconds — a numeric axis, so two points can share a day. */
  readonly x: number;
  /** The remaining amount before `today`, drawn muted. Null where the past series does not reach. */
  readonly past: number | null;
  /** The remaining amount from `today` on, drawn solid. */
  readonly remaining: number | null;
}

const DAY_MS = 86_400_000;

export function dayToX(day: string): number {
  return Date.parse(`${day}T00:00:00Z`);
}

export function xToDay(x: number): string {
  return new Date(x).toISOString().slice(0, 10);
}

/** Nothing scheduled in the period: the chart says so in words rather than drawing a line along zero. */
export function isEmptyBurnDown(steps: readonly BurnDownStep[]): boolean {
  return steps.every((step) => step.amountCents === 0);
}

/**
 * The points of a step line from `from` to the end of `to`, split at `today` into a muted and a solid series
 * that SHARE the value in force at the start of today — so the two halves meet rather than leaving a gap.
 *
 * A payment due TODAY belongs to the solid half (clarification Q3): it is still to go out until the day is
 * over, so its drop is drawn on the solid line, after the junction.
 */
export function burnDownPoints(
  steps: readonly BurnDownStep[],
  window: { from: string; to: string },
  today: string,
): BurnDownPoint[] {
  const ordered = [...steps].sort((a, b) => (a.at < b.at ? -1 : 1));
  const start = ordered.reduce((sum, step) => sum + step.amountCents, 0);
  const todayX = dayToX(today);
  const endX = dayToX(window.to) + DAY_MS;

  const raw: { x: number; value: number }[] = [{ x: dayToX(window.from), value: start }];
  let value = start;
  for (const step of ordered) {
    value -= step.amountCents;
    raw.push({ x: dayToX(step.at), value });
  }
  raw.push({ x: endX, value });

  // The value at the START of today: after every payment before today, before today's own.
  const atTodayStart =
    start -
    ordered.filter((step) => step.at < today).reduce((sum, step) => sum + step.amountCents, 0);

  const points: BurnDownPoint[] = [];
  for (const point of raw) {
    if (point.x < todayX) points.push({ x: point.x, past: point.value, remaining: null });
  }
  if (todayX >= dayToX(window.from) && todayX <= endX) {
    points.push({ x: todayX, past: atTodayStart, remaining: atTodayStart });
  }
  for (const point of raw) {
    if (point.x >= todayX) points.push({ x: point.x, past: null, remaining: point.value });
  }
  return points;
}
